import './mobilenav.less'

import {m} from '@codeurs/front'
import {Slide} from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import {ArrowLinkIcon, SearchIcon} from 'assets/inline-icons'
import React, {useState} from 'react'
import AnimateHeight from 'react-animate-height'
import HamburgerMenu from 'react-hamburger-menu'
import {Link} from 'react-router-dom'
import {TransitionProps} from 'react-transition-group/Transition'
import {CategoryItemType, CategoryType, PageData, SubCategoryType} from 'types'
import {t} from 'util/i18n'
import {PageLink} from 'util/pagelink'
import {style} from 'util/style'
import {CardCategory} from 'views/layout/card'
import {FooterTop} from './footer'

export type MobileNavType = PageData

const styles = style('mobilenav', [
  'hamburger',
  'search',
  'searchcontainer',
  'links',
  'link',
  'sublink'
])

export const MobileNav: React.FC<
  MobileNavType & {
    setSearchOpened: (isOpen: boolean) => void
    setMobileNavOpened: (isOpen: boolean) => void
    mobileIsOpen: boolean
    searchIsOpen: boolean
    showNewsletter?: boolean
  }
> = ({
  setMobileNavOpened,
  setSearchOpened,
  mobileIsOpen,
  searchIsOpen,
  showNewsletter,
  ...data
}) => {
  const bodyStyle = document.body.style
  const subsite = data.subsite
  const header = data.header
  if (mobileIsOpen || searchIsOpen) bodyStyle.overflowY = 'hidden'
  bodyStyle.overflowY = 'auto'
  const {categories} = data
  return (
    <div className={styles.is({open: mobileIsOpen || searchIsOpen})}>
      {!mobileIsOpen && (
        <div
          className={styles.search}
          onClick={() => setSearchOpened(!searchIsOpen)}
        >
          <SearchIcon
            className={styles.search('icon').is({open: searchIsOpen})}
          />
        </div>
      )}
      {!searchIsOpen && (
        <div className={styles.hamburger}>
          <HamburgerMenu
            isOpen={mobileIsOpen}
            menuClicked={() => setMobileNavOpened(!mobileIsOpen)}
            color="white"
            height={18}
            width={30}
            borderRadius={5}
          />
        </div>
      )}
      <Dialog
        open={mobileIsOpen}
        TransitionComponent={TransitionComponent}
        onClose={() => setMobileNavOpened(false)}
        fullScreen
      >
        <div className={styles.hamburger}>
          <HamburgerMenu
            isOpen={mobileIsOpen}
            menuClicked={() => setMobileNavOpened(!mobileIsOpen)}
            color="white"
            height={18}
            width={30}
            borderRadius={5}
          />
        </div>
        <div className={styles.links.is({open: mobileIsOpen})}>
          {subsite !== '2050' &&
            subsite !== 'error2050' &&
            categories.map(cat => (
              <MobileNavCategory
                {...cat}
                closeNav={() => setMobileNavOpened(false)}
              />
            ))}
          {(subsite === '2050' || subsite === 'error2050') &&
            header.nav.concat(header.btn_nav).map(navlink => (
              <span className={styles.link.mod('2050')}>
                <PageLink
                  to={navlink.url}
                  target={navlink.target}
                  onClick={() => setMobileNavOpened(false)}
                >
                  {navlink.description || navlink.title}
                </PageLink>
              </span>
            ))}
          {showNewsletter && (
            <Link
              to={'#newsletter'}
              {...styles.link.mod('newsletter')}
              onClick={() => setMobileNavOpened(false)}
              aria-label={t('newsletter.label')}
            >
              {t('newsletter.btn')}
            </Link>
          )}
          <div className={styles.links('bottom')}>
            <FooterTop
              mod="navigation"
              closenav={() => setMobileNavOpened(false)}
              {...data}
            />
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export const MobileNavCategory: React.FC<
  CategoryType & {closeNav: (close: boolean) => void}
> = ({closeNav, ...category}) => {
  const [isOpened, setOpened] = useState(false)
  const isNews = category.channel === 'news'
  return (
    <div className={styles.link.mod('category').is({open: isOpened})}>
      <div className={styles.link('title')}>
        <CardCategory color={category.color} />
        {isNews ? (
          <div className={styles.link('title-text')}>
            <Link to={category.url} onClick={e => closeNav(true)}>
              {category.title}
            </Link>
          </div>
        ) : (
          <span
            onClick={e => {
              e.currentTarget.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              })
              setOpened(!isOpened)
            }}
            className={styles.link('title-text')}
          >
            {category.title}
          </span>
        )}
      </div>
      {!isNews && (
        <AnimateHeight height={isOpened ? 'auto' : 0} animateOpacity={true}>
          <div className={styles.link('categorycontainer')}>
            {category.children.map(subnav => (
              <SubnavMobileCategory
                {...subnav}
                closeNav={close => closeNav(close)}
              />
            ))}
          </div>
        </AnimateHeight>
      )}
    </div>
  )
}
export const SubnavMobileCategory: React.FC<
  SubCategoryType & {closeNav: (close: boolean) => void}
> = ({closeNav, ...subNav}) => {
  const [isOpened, setOpened] = useState(false)
  return (
    <div className={styles.link.mod('subcategory').is({open: isOpened})}>
      <MobileSubNavLinkView
        isOpened={isOpened}
        setOpened={setOpened}
        closeNav={closeNav}
        {...subNav}
      />
      {subNav.children && subNav.children.length > 0 && (
        <AnimateHeight height={isOpened ? 'auto' : 0} animateOpacity={true}>
          <div className={styles.link('subnavcontainer')}>
            {subNav.children.map(subnavlink => (
              <LinkButton closeNav={close => closeNav(close)} {...subnavlink} />
            ))}
          </div>
        </AnimateHeight>
      )}
    </div>
  )
}
const TransitionComponent: React.ComponentType<TransitionProps> =
  React.forwardRef(function Transition(props, ref) {
    return <Slide direction="right" ref={ref} {...props} />
  })

const MobileSubNavLinkView: React.FC<
  SubCategoryType & {
    setOpened: (isOpened: boolean) => void
    isOpened: boolean
    closeNav: (close: boolean) => void
  }
> = ({setOpened, isOpened, closeNav, ...subNav}) => {
  const hasChildren = subNav.children && subNav.children.length > 0
  if (!subNav.own_page && hasChildren)
    return (
      <DropdownLink
        hasChildren={hasChildren}
        isOpened={isOpened}
        setOpened={setOpened}
        {...subNav}
      />
    )

  if (subNav.own_page && hasChildren) {
    const [isActive, setActive] = useState(false)
    return (
      <div
        className={styles
          .link('subnavcontainer-linkholder')
          .is({active: isOpened})}
      >
        <div
          className={styles
            .link('subnavcontainer-linkholder-dropdown')
            .is({active: isOpened})}
        >
          <DropdownLink
            hasChildren={hasChildren}
            isOpened={isOpened}
            setOpened={open => {
              setOpened(open)
            }}
            {...subNav}
          />
        </div>
        <div
          className={styles
            .link('subnavcontainer-linkholder-link')
            .is({active: isOpened})}
        >
          <LinkButton closeNav={close => closeNav(close)} {...subNav} />
        </div>
      </div>
    )
  }

  return <LinkButton closeNav={close => closeNav(close)} {...subNav} />
}

const LinkButton: React.FC<
  CategoryItemType & {closeNav: (close: boolean) => void}
> = ({closeNav, ...subnavlink}) => (
  <span className={styles.link('subnavcontainer-link')}>
    <Link to={subnavlink.url} onClick={e => closeNav(true)}>
      {subnavlink.title}
    </Link>
  </span>
)

const DropdownLink: React.FC<
  CategoryItemType & {
    hasChildren: boolean
    isOpened: boolean
    setOpened: (setOpen: boolean) => void
  }
> = ({isOpened, setOpened, hasChildren, ...subNav}) => {
  return (
    <div
      className={styles.sublink.mod({
        nochildren: !hasChildren
      })}
    >
      <span
        onClick={e => {
          e.currentTarget.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
          setOpened(!isOpened)
        }}
      >
        <span className={styles.sublink('title')}>{subNav.title}</span>
        {hasChildren && (
          <ArrowLinkIcon
            className={styles.sublink('arrow').is({open: isOpened})}
          />
        )}
      </span>
    </div>
  )
}
